import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Spin, Table} from 'antd';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import UserLink from 'components/userLink/UserLink';
import {GET_REFERRAL_CHANNEL_STATS} from '../resolvers';
import {GET_ALL_USERS_BY_NAME} from 'graphql/queries/campaign';
import {Card, EmptyState, Section, ShareBar} from '../../shared/DashboardUI';
import {plainNumber, seriesColor} from '../../shared/chartTheme';
import styles from '../../shared/dashboard.module.scss';

const PAGE_SIZE = 10;

/**
 * How people say they found daba.
 *
 * Each channel is now shown against the total, because "3 users" means nothing
 * until you know whether that is 3 out of 4 or 3 out of 4,000 — and this list
 * exists to compare channels against each other.
 *
 * The modal's pager needed the bigger fix. It fed antd a `total` computed like
 * this:
 *
 *   const totalUsers = hasNextPage ? (currentPage + 1) * limit : ...
 *
 * That is a guess dressed as a count. With `showQuickJumper` on, antd rendered
 * page numbers from it, so the control offered to jump to pages derived from a
 * number nobody had measured, and the page count changed as you paged. The
 * endpoint only reports `hasNextPage`, so the pager now says exactly that much:
 * Previous and Next, with the range it is showing.
 */

export const ReferralChannelStats = () => {
  const [channel, setChannel] = useState(null);
  const [page, setPage] = useState(1);

  const {data, loading} = useQuery(GET_REFERRAL_CHANNEL_STATS, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load referral channel statistics'),
  });

  const {data: usersData, loading: usersLoading} = useQuery(
    GET_ALL_USERS_BY_NAME,
    {
      variables: {
        filters: {
          user: {referralChannel: channel},
          pagination: {page, limit: PAGE_SIZE},
        },
      },
      skip: !channel,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: () => toast.error('Could not load users for this channel'),
    },
  );

  const statistics = data?.getReferralChannelStatistics?.data?.statistics ?? [];
  const total = statistics.reduce(
    (sum, item) => sum + (Number(item?.value) || 0),
    0,
  );
  const ranked = [...statistics].sort(
    (a, b) => (Number(b?.value) || 0) - (Number(a?.value) || 0),
  );

  const users = usersData?.getAllUsers?.users ?? [];
  const hasNextPage = usersData?.getAllUsers?.hasNextPage ?? false;
  const firstOnPage = (page - 1) * PAGE_SIZE + 1;

  const open = value => {
    setChannel(value);
    setPage(1);
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => {
        const name = [record?.firstName, record?.middleName, record?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return (
          <UserLink userId={record?.id} className={styles.linkButton}>
            {name || 'Unnamed'}
          </UserLink>
        );
      },
    },
    {title: 'Email', dataIndex: 'email', key: 'email', render: v => v || '—'},
    {
      title: 'Phone',
      key: 'phoneNumber',
      render: (_, record) =>
        record?.phoneNumber
          ? `${record?.countryCode ?? ''}${record.phoneNumber}`
          : '—',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: v => v || '—',
    },
  ];

  return (
    <div className={styles.page} style={{padding: 0}}>
      <Section
        title="Referral channels"
        description="Self-reported by users at signup. Select a channel to see who it brought in."
        meta={`${plainNumber(total)} attributed`}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
            <span>Loading referral channels…</span>
          </div>
        ) : ranked.length === 0 ? (
          <EmptyState
            title="No referral data yet"
            description="Channels appear here once users start reporting how they found daba."
          />
        ) : (
          <Card>
            <div className={styles.statusList}>
              {ranked.map((item, index) => {
                const value = Number(item?.value) || 0;
                return (
                  <button
                    key={`${item?.label}-${index}`}
                    type="button"
                    className={styles.channelRow}
                    onClick={() => open(item?.label)}>
                    <div className={styles.statusTop}>
                      <span className={styles.statusLabel}>{item?.label}</span>
                      <span className={styles.statusValue}>
                        {plainNumber(value)}
                        {total > 0 && (
                          <span className={styles.statusShare}>
                            {Math.round((value * 100) / total)}%
                          </span>
                        )}
                      </span>
                    </div>
                    <ShareBar
                      value={value}
                      total={total}
                      color={seriesColor(index)}
                    />
                  </button>
                );
              })}
            </div>
          </Card>
        )}
      </Section>

      <DBModal
        isOpen={!!channel}
        handleClose={() => setChannel(null)}
        width={820}
        content={
          <div className={styles.modal}>
            <div className={styles.sectionHead}>
              <div>
                <h3>Users referred via {channel}</h3>
                <p>Select a name to open that user.</p>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={users}
              loading={usersLoading}
              pagination={false}
              rowKey="id"
              size="small"
              locale={{
                emptyText: usersLoading ? (
                  <span />
                ) : (
                  <EmptyState title="No users on this page" compact />
                ),
              }}
            />

            {(users.length > 0 || page > 1) && (
              <div className={styles.pager}>
                <span className={styles.sectionMeta}>
                  {users.length
                    ? `Showing ${plainNumber(firstOnPage)}–${plainNumber(
                        firstOnPage + users.length - 1,
                      )}`
                    : 'No results on this page'}
                </span>
                <div className={styles.pagerButtons}>
                  <button
                    type="button"
                    className={styles.pagerButton}
                    disabled={page === 1 || usersLoading}
                    onClick={() =>
                      setPage(current => Math.max(1, current - 1))
                    }>
                    Previous
                  </button>
                  <button
                    type="button"
                    className={styles.pagerButton}
                    disabled={!hasNextPage || usersLoading}
                    onClick={() => setPage(current => current + 1)}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default ReferralChannelStats;
